import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import SEO from "@/components/base/SEO";
import ScrollToTop from "@/components/base/ScrollToTop";
import { testimonials, contactInfo } from "@/mocks/comics";

const siteUrl = "https://hanmanzhijia.com";
const CONTACT_FORM_URL = "https://readdy.ai/api/form/d9pc48qftmtnfcfh0kc0";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("phone_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      return;
    }

    setLoading(true);
    setFormError("");

    try {
      const formData = new FormData(form);
      const res = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        body: formData,
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || "提交失败，请稍后再试";
        setFormError(msg);
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于韩漫之家",
    description: "韩漫之家是专业的韩国漫画在线阅读平台，提供好看韩国漫画、韩漫在线免费阅读与高清韩漫大全。",
    url: `${siteUrl}/about`,
    isPartOf: {
      "@type": "WebSite",
      name: "韩漫之家",
      url: siteUrl,
    },
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "韩漫之家",
    url: siteUrl,
    description: "好看韩国漫画，韩漫在线免费阅读，高清韩漫大全。提供稳定优质韩国漫画阅读体验。",
    address: {
      "@type": "PostalAddress",
      streetAddress: "望京SOHO T3 15层",
      addressLocality: "北京",
      addressRegion: "朝阳区",
      addressCountry: "CN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: contactInfo.phone,
      contactType: "customer service",
      availableLanguage: "Chinese",
      email: contactInfo.email,
    },
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "韩漫之家的漫画是免费阅读的吗？",
        acceptedAnswer: { "@type": "Answer", text: "韩漫之家上的大部分漫画均为免费阅读，我们致力于为漫画爱好者提供优质的免费阅读体验。部分独家授权作品可能需要会员解锁，但我们每周都会更新大量免费章节。" },
      },
      {
        "@type": "Question",
        name: "韩漫之家的漫画多久更新一次？",
        acceptedAnswer: { "@type": "Answer", text: "不同作品的更新频率有所不同，热门连载通常保持每周更新。您可以在漫画详情页查看具体的更新日期。" },
      },
    ],
  };

  return (
    <>
      <SEO
        title="关于我们_韩漫之家-好看韩国漫画在线免费阅读平台"
        description="了解韩漫之家——专业韩国漫画在线阅读平台。品牌介绍、用户口碑、联系方式。享受好看韩国漫画的韩漫在线免费阅读与高清韩漫大全的可靠服务。"
        keywords="韩漫之家,关于我们,韩国漫画平台,韩漫在线免费阅读,联系方式"
        canonicalUrl={`${siteUrl}/about`}
        jsonLd={{ ...aboutJsonLd, ...orgJsonLd, ...faqJsonLd }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "关于我们" },
          ]}
        />

        <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于韩漫之家
            </h1>
            <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              好看韩国漫画，韩漫在线免费阅读，高清韩漫大全——韩漫之家，为每一位漫画爱好者提供可靠服务与稳定优质的阅读体验。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-background-100 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
                <i className="ri-eye-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">高清画质</h3>
              <p className="text-sm text-foreground-500">所有漫画均采用高清扫描与数字优化，为您呈现最佳的视觉阅读体验。</p>
            </div>
            <div className="bg-background-100 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">可靠服务</h3>
              <p className="text-sm text-foreground-500">稳定的服务器与流畅的加载速度，确保您随时随地都能享受无忧阅读。</p>
            </div>
            <div className="bg-background-100 rounded-xl p-6 text-center">
              <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-secondary-100 text-secondary-600">
                <i className="ri-refresh-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">每日更新</h3>
              <p className="text-sm text-foreground-500">热门连载每日更新，让您不再等待，第一时间追更心仪的韩漫作品。</p>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-2">品牌故事</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">韩漫之家的创立初心与成长历程</p>
            <div className="max-w-3xl mx-auto bg-background-100 rounded-xl p-6 md:p-8">
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                韩漫之家成立于2020年，是一个专注于韩国漫画在线阅读的中文平台。我们的创始团队由一群热爱漫画的资深读者和技术专家组成，深知读者对于<strong>高清韩漫大全</strong>和优质阅读体验的渴望。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                从最初收录几百部作品的小站，到今天拥有上千部<strong>好看韩国漫画</strong>的综合平台，韩漫之家始终坚持以用户需求为核心，不断优化<strong>韩漫在线免费阅读</strong>体验。我们与多家韩国漫画出版社建立了合作关系，确保内容正版、更新及时。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们的使命是让每一位中文读者都能轻松享受高质量的韩国漫画。无论是恋爱、奇幻、热血还是悬疑题材，在韩漫之家，你都能找到心仪的作品。我们承诺提供<strong>可靠服务</strong>，持续为漫画爱好者创造价值。
              </p>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-2">用户口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">听听读者们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <div className="flex gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950 text-center mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议，欢迎与我们取得联系</p>

            <div className="flex flex-col lg:flex-row gap-10 max-w-4xl mx-auto">
              <div className="flex-1 bg-background-100 rounded-xl p-6 md:p-8">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                    <span className="text-sm text-foreground-600">{contactInfo.email}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                    <span className="text-sm text-foreground-600">{contactInfo.phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-wechat-line text-primary-500 text-lg"></i>
                    <span className="text-sm text-foreground-600">{contactInfo.wechat}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-map-pin-line text-primary-500 text-lg"></i>
                    <span className="text-sm text-foreground-600">{contactInfo.address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-time-line text-primary-500 text-lg"></i>
                    <span className="text-sm text-foreground-600">{contactInfo.workingHours}</span>
                  </div>
                </div>
              </div>

              <div className="flex-1 bg-background-100 rounded-xl p-6 md:p-8">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                {formStatus === "success" ? (
                  <p className="text-sm text-accent-600 font-medium py-8 text-center">消息已发送！感谢您的反馈，我们会尽快回复您。</p>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-3">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的消息（最多500字）"
                      rows={4}
                      maxLength={500}
                      required
                      className="px-4 py-2.5 text-sm rounded-lg bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    ></textarea>
                    {formStatus === "error" && (
                      <p className="text-xs text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {loading ? "发送中..." : "发送消息"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}